import React, { useState, useRef, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LogoClienteFacilities from '../LogoClienteFacilities';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useCart } from '../../CartContext';
import { t } from '../../Utills/translations';
import ThemeToggle from '../ThemeToggle';
import { isCardapioOnly, isCardapioPath, rememberCardapioPath, getCardapioReturnUrl } from '../../Utills/cardapioNavigation';
import { resolveClientColor } from '../../Utills/colors';
import './style.css';

export function HeaderCardapio({ searchTerm, setSearchTerm }) {
  const { cliente_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const cardapioOnly = isCardapioOnly(location.search);
  const hideBack = cardapioOnly && isCardapioPath(location.pathname, cliente_id);

  useEffect(() => {
    rememberCardapioPath(cliente_id, location.pathname, location.search);
  }, [cliente_id, location.pathname, location.search]);
  const { cartCount } = useCart();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const inputRef = useRef(null);

  const queryParams = new URLSearchParams(location.search);
  const apartamento = queryParams.get('x');
  const cliente_url = queryParams.get('id_cliente');
  const session_control = queryParams.get('session_control');

  const cor_letra = localStorage.getItem('cliente_cor_letra');
  const pedidoMenu = localStorage.getItem('cliente_pedido_menu') !== 'false';

  const totalItens = cartCount || 0;

  const resolvedColor = resolveClientColor();

  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      right: -3,
      top: 13,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: '0 4px',
      color: cor_letra,
      background: resolvedColor,
    },
  }));

  const mainURL = window.location.href;
  const mainUrlParam = mainURL.split('/');
  const showSearch =
    mainUrlParam[4] === 'cardapio' && mainUrlParam[5] !== 'tipo';

  const handleOpenSearch = () => {
    setIsSearchOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 300);
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    if (setSearchTerm) setSearchTerm('');
  };

  // Fechar com Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSearchOpen) {
        handleCloseSearch();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen]);

  return (
    <header className="header">
      <div className="container">
        {/* Esquerda: Voltar + Logo */}
        {!hideBack && <button
          onClick={() => cardapioOnly
            ? navigate(getCardapioReturnUrl(cliente_id, location.search))
            : window.history.back()}
          className="btn-header-return"
          aria-label={cardapioOnly ? 'Voltar ao cardápio' : 'Voltar'}
        >
          <ArrowBackIosIcon />
        </button>}

        <div className={`header-logo-wrapper ${isSearchOpen ? 'header-logo-hidden' : ''}`}>
          <LogoClienteFacilities disableNavigation={cardapioOnly} />
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Direita: Busca + Tema + Carrinho */}
        <div className="header-right-icons">
          {showSearch && !isSearchOpen && (
            <IconButton
              onClick={handleOpenSearch}
              className="header-search-btn"
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
          )}

          {showSearch && isSearchOpen && (
            <div className="header-search-container header-search-open">
              <div className="header-search-input-wrapper">
                <SearchIcon className="header-search-input-icon" />
                <input
                  ref={inputRef}
                  type="text"
                  className="header-search-input"
                  placeholder={t('buscar')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="search"
                />
                <IconButton
                  onClick={handleCloseSearch}
                  className="header-search-close-btn"
                  size="small"
                  aria-label="close search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          )}

          <ThemeToggle />

          {pedidoMenu && totalItens > 0 && (
            <IconButton aria-label="cart">
              <Link
                to={`/${cliente_id}/carrinho/${location.search}`}
              >
                <StyledBadge badgeContent={totalItens || null} color="secondary">
                  <ShoppingCartIcon />
                </StyledBadge>
              </Link>
            </IconButton>
          )}
        </div>
      </div>
    </header>
  );
}
